import React, { useCallback, useEffect, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faXmark } from '@fortawesome/free-solid-svg-icons'

import type { ControlCenterPerson } from '../../../types/controlCenter'
import { useControlCenterOperationConfig } from '../../../hooks/useControlCenterOperationConfig'
import {
  getAvailableOperators,
  operationEntityFromPersonIdentity,
  type OperationInputs,
  type OperatorDef,
  type OperatorId,
} from '../operations'
import { PERSON_KIND_COLOR, personKindLabel } from './mapEntityPresentation'
import { DockedMapPopupShell } from './MapEntityPopups'
import { OperatorPopupControls, useOperatorPopupState } from './OperatorPopupControls'
import type { MappableEntity } from './controlCenterMapTypes'

/**
 * Bottom-left docked entity popup for people (etc.) that should have a map
 * location but currently do not (e.g. off-clock employees with no pin).
 */
export function UnlocatedEntityDockedPopup({
  person,
  open,
  openPopupToken = 0,
  operationRunning = false,
  operationResultOperatorId = null,
  destinationOptions = [],
  preferredDestinationRef = '',
  onDismiss,
  onRunOperator,
}: {
  person: ControlCenterPerson
  open: boolean
  openPopupToken?: number
  operationRunning?: boolean
  operationResultOperatorId?: OperatorId | null
  destinationOptions?: Array<{ id: string; label: string; group?: string; keywords?: string }>
  preferredDestinationRef?: string
  onDismiss?: () => void
  onRunOperator: (
    entity: MappableEntity,
    operatorId: OperatorId,
    inputs: OperationInputs,
  ) => void
}) {
  const { config: operationConfig } = useControlCenterOperationConfig()
  const entity = useMemo((): MappableEntity => {
    return {
      id: person.id,
      category: 'person',
      lat: Number.NaN,
      lng: Number.NaN,
      color: PERSON_KIND_COLOR[person.personKind],
      icon: faUser,
      title: person.name,
      subtitle: [
        personKindLabel(person.personKind),
        person.clockedIn ? 'Clocked in' : 'Off clock',
        'No map location',
      ].join(' · '),
      detail:
        [person.roleName || null, person.jobName || null].filter(Boolean).join(' · ') || undefined,
    }
  }, [person])

  const operators = useMemo((): OperatorDef[] => {
    const opEntity = operationEntityFromPersonIdentity(person)
    return getAvailableOperators(opEntity.capabilities, {
      enabledOperators: operationConfig.enabledOperators,
    })
  }, [person, operationConfig.enabledOperators])

  const onRun = useCallback(
    (op: OperatorDef, inputs: OperationInputs) => {
      onRunOperator(entity, op.id, inputs)
    },
    [entity, onRunOperator],
  )

  const {
    activeOperatorId,
    activeOperator,
    draftInputs,
    popupDismissed,
    dismissPopup,
    revealPopup,
    selectOperator,
    patchInputs,
    selectDestinationEntity,
    runActiveOperator,
    destinationOptional,
    needsDestination,
    canManualRun,
    showRunBesideRadius,
    showRunBesideRange,
    showRunAtEnd,
    isRerun,
    runLabel,
  } = useOperatorPopupState({
    operators,
    preferredDestinationRef,
    operationResultOperatorId,
    operationRunning,
    isActive: open,
    openPopupToken,
    onRun,
  })

  useEffect(() => {
    if (open) revealPopup()
  }, [open, openPopupToken, revealPopup])

  const handleDismiss = useCallback(() => {
    dismissPopup()
    onDismiss?.()
  }, [dismissPopup, onDismiss])

  if (!open || popupDismissed) return null

  return (
    <DockedMapPopupShell onDismiss={handleDismiss}>
      <div className="min-w-[16rem] max-w-[20rem]">
        <div className="flex items-start gap-2">
          <span
            className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: entity.color }}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
              Person
            </div>
            <div className="text-sm font-semibold leading-tight text-base-content">
              {entity.title}
            </div>
            {entity.subtitle ? (
              <div className="mt-1 text-xs leading-snug text-base-content/70">{entity.subtitle}</div>
            ) : null}
            {entity.detail ? (
              <div className="mt-1 text-xs leading-snug text-base-content/55">{entity.detail}</div>
            ) : null}
            <div className="mt-1.5 text-[11px] leading-snug text-warning">
              No map pin — clock in at a job with a location to place them on the map.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-square btn-xs shrink-0"
            aria-label="Close"
            title="Close"
            onClick={handleDismiss}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {operators.length > 0 ? (
          <OperatorPopupControls
            variant="entity"
            operators={operators}
            operationRunning={operationRunning}
            destinationOptions={destinationOptions}
            activeOperatorId={activeOperatorId}
            activeOperator={activeOperator}
            draftInputs={draftInputs}
            selectOperator={selectOperator}
            patchInputs={patchInputs}
            selectDestinationEntity={selectDestinationEntity}
            runActiveOperator={runActiveOperator}
            destinationOptional={destinationOptional}
            needsDestination={needsDestination}
            canManualRun={canManualRun}
            showRunBesideRadius={showRunBesideRadius}
            showRunBesideRange={showRunBesideRange}
            showRunAtEnd={showRunAtEnd}
            isRerun={isRerun}
            runLabel={runLabel}
          />
        ) : null}
      </div>
    </DockedMapPopupShell>
  )
}
